import { motion } from "framer-motion";
import {
  Instagram,
  MessageCircle,
  Github,
  Linkedin,
  Mail,
  ArrowUpRight,
} from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const FEATURED = {
  name: "WhatsApp",
  description: "Direkte Nachricht – der schnellste Weg zu mir.",
  handle: "+49 152 29290009",
  href: "https://wa.me/4915229290009",
  icon: MessageCircle,
  testId: "social-link-whatsapp",
  badge: "Bevorzugter Kontakt",
};

const LINKS = [
  {
    name: "Instagram",
    description: "Alltag, Ideen und Prozesse hinter den Kulissen.",
    handle: "@egekoeln",
    href: "https://instagram.com/egekoeln",
    icon: Instagram,
    testId: "social-link-instagram",
  },
  {
    name: "GitHub",
    description: "Code, Experimente und kleine offene Tools.",
    handle: "/egeyildiz",
    href: "https://github.com/egeyildiz",
    icon: Github,
    testId: "social-link-github",
  },
  {
    name: "LinkedIn",
    description: "Beruflich, Netzwerk und Zusammenarbeit.",
    handle: "/in/egeyildiz",
    href: "https://linkedin.com/in/egeyildiz",
    icon: Linkedin,
    testId: "social-link-linkedin",
  },
  {
    name: "E-Mail",
    description: "Für längere Nachrichten und Anfragen.",
    handle: "hi@ege.koeln",
    href: "mailto:hi@ege.koeln",
    icon: Mail,
    testId: "social-link-email",
  },
];

/**
 * LinksGrid – premium, app-like contact cards.
 * Layout: featured WhatsApp (col-span-2) + 5 secondary channels.
 */
export default function LinksGrid() {
  const handlePointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  const Featured = FEATURED.icon;

  return (
    <section
      id="links"
      data-testid="links-section"
      className="relative px-6 py-28 sm:px-10 sm:py-36 md:px-16 lg:px-24"
    >
      <div className="mx-auto mb-20 h-px w-full max-w-5xl bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

      <div className="mx-auto w-full max-w-5xl">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease }}
          className="font-mono-label mb-8 text-xs text-zinc-500"
        >
          <span className="mr-3 text-[#FF4500]">03</span> Kontakt &amp; Links
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease }}
          className="font-display mb-16 max-w-3xl text-3xl font-bold leading-[1.05] tracking-tight text-[#FAFAFA] sm:text-4xl md:text-5xl lg:text-6xl"
          data-testid="links-headline"
        >
          Sag hallo.
          <br />
          <span className="text-zinc-500">Ich freue mich.</span>
        </motion.h2>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: 0.07, delayChildren: 0.05 },
            },
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {/* Featured card (WhatsApp) */}
          <motion.a
            href={FEATURED.href}
            target="_blank"
            rel="noopener noreferrer"
            onPointerMove={handlePointerMove}
            data-testid={FEATURED.testId}
            variants={{
              hidden: { opacity: 0, y: 24 },
              show: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.7, ease },
              },
            }}
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="link-card link-card-featured group relative flex flex-col justify-between gap-8 rounded-2xl border border-white/[0.08] p-7 backdrop-blur-xl transition-shadow duration-500 hover:shadow-[0_18px_60px_-15px_rgba(255,69,0,0.4)] sm:col-span-2 sm:min-h-[220px] sm:p-8"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#FF4500]/30 bg-[#FF4500]/10 text-[#FF4500] shadow-[inset_0_0_20px_rgba(255,69,0,0.15)]">
                <Featured size={22} strokeWidth={1.75} />
              </div>
              <span className="font-mono-label text-[10px] text-[#FF4500]">
                {FEATURED.badge}
              </span>
            </div>

            <div className="flex items-end justify-between gap-6">
              <div className="min-w-0">
                <p className="font-display text-2xl font-bold tracking-tight text-[#FAFAFA] sm:text-3xl">
                  {FEATURED.name}
                </p>
                <p className="mt-2 max-w-md text-sm text-zinc-400 sm:text-base">
                  {FEATURED.description}
                </p>
                <p className="font-mono-tag mt-4 text-xs text-zinc-500">
                  {FEATURED.handle}
                </p>
              </div>
              <ArrowUpRight
                size={22}
                className="shrink-0 text-zinc-500 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#FF4500]"
              />
            </div>
          </motion.a>

          {/* Secondary cards */}
          {LINKS.map((link) => {
            const Icon = link.icon;
            const isPlaceholder = link.href === "#";
            return (
              <motion.a
                key={link.name}
                href={link.href}
                target={
                  link.href.startsWith("http") ? "_blank" : undefined
                }
                rel="noopener noreferrer"
                onPointerMove={handlePointerMove}
                data-testid={link.testId}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease },
                  },
                }}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className="link-card group flex flex-col justify-between gap-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-6 backdrop-blur-xl transition-all duration-300 hover:border-white/20 hover:bg-white/[0.035] sm:min-h-[190px]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] text-zinc-300 transition-all duration-300 group-hover:border-white/20 group-hover:text-white">
                    <Icon size={18} strokeWidth={1.75} />
                  </div>
                  {isPlaceholder && (
                    <span className="font-mono-label rounded-full border border-white/[0.08] bg-white/[0.02] px-2 py-1 text-[9px] text-zinc-500">
                      Coming Soon
                    </span>
                  )}
                </div>

                <div className="flex items-end justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-semibold tracking-tight text-[#FAFAFA]">
                      {link.name}
                    </p>
                    <p className="mt-1.5 line-clamp-2 text-sm text-zinc-500">
                      {link.description}
                    </p>
                    <p className="font-mono-tag mt-3 text-xs text-zinc-600">
                      {link.handle}
                    </p>
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="shrink-0 text-zinc-600 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#FF4500]"
                  />
                </div>
              </motion.a>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
